var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import Base from "../Base.js";
import VersionHelper from "../helper/VersionHelper.js";
import BrowserHelper from "../helper/BrowserHelper.js";
const { defineProperty } = Reflect;
let performance;
if (BrowserHelper.isBrowserEnv) {
  performance = globalThis.performance;
} else {
  performance = {
    now() {
      return (/* @__PURE__ */ new Date()).getTime();
    }
  };
}
let globalDelays = null;
if (VersionHelper.isTestEnv) {
  const bryntum = globalThis.bryntum || (globalThis.bryntum = {});
  globalDelays = bryntum.globalDelays = {
    timeouts: /* @__PURE__ */ new Map(),
    intervals: /* @__PURE__ */ new Map(),
    animationFrames: /* @__PURE__ */ new Map(),
    idleCallbacks: /* @__PURE__ */ new Map(),
    isEmpty(includeIntervals = false) {
      return globalDelays.timeouts.size + globalDelays.animationFrames.size + globalDelays.idleCallbacks.size + (includeIntervals ? globalDelays.intervals.size : 0) === 0;
    },
    /**
     * Returns filtered delays array
     * @param {Object} options
     * @param {String[]} [options.ignoreTimeouts] array of delays names to ignore
     * @param {Number} [options.maxDelay] maximum delay in milliseconds. Timeouts with bigger delay will be filtered out
     * @param {Boolean} [options.includeIntervals] include intervals
     * @returns {Object[]} array of filtered delays
     * @internal
     */
    getFiltered({ ignoreTimeouts = [], maxDelay = 5e3, includeIntervals = false }) {
      const result = [], scopes = ["timeouts", "animationFrames", "idleCallbacks"];
      if (includeIntervals) {
        scopes.push("intervals");
      }
      for (const scope of scopes) {
        const map = globalDelays[scope];
        for (const [, entry] of map.entries()) {
          if (!ignoreTimeouts.includes(entry.name) && (!Number.isInteger(entry.delay) || entry.delay < maxDelay)) {
            result.push(entry);
          }
        }
      }
      return result;
    }
  };
}
const asapPromise = Promise.resolve(), emptyArray = Object.freeze([]), emptyFn = () => {
}, emptyObject = Object.freeze({}), queueMicrotask = globalThis.queueMicrotask, makeInvoker = (me, fn, wrapFn, options) => {
  const named = typeof fn === "string", appendArgs = (options == null ? void 0 : options.appendArgs) || emptyArray, invoker = () => {
    wrapFn.timerId = null;
    wrapFn.lastCallTime = performance.now();
    const args = wrapFn.args;
    wrapFn.args = null;
    if (named) {
      me[fn](...args, ...appendArgs);
    } else {
      fn.call(me, ...args, ...appendArgs);
    }
    wrapFn.called = true;
    ++wrapFn.calls;
  };
  me = (options == null ? void 0 : options.thisObj) || me;
  wrapFn.lastCallTime = -9e9;
  wrapFn.calls = 0;
  wrapFn.suspended = options == null ? void 0 : options.suspended;
  wrapFn.invoker = invoker;
  invoker.wrapFn = wrapFn;
  invoker.fn = fn;
  invoker.this = me;
  return invoker;
}, decorateWrapFn = (me, wrapFn, cancelFn = "clearTimeout") => {
  wrapFn.cancel = () => {
    if (wrapFn.isPending) {
      me[cancelFn](wrapFn.timerId);
      wrapFn.args = wrapFn.timerId = null;
    }
  };
  wrapFn.flush = () => {
    if (wrapFn.isPending) {
      me[cancelFn](wrapFn.timerId);
      wrapFn.timerId = null;
      wrapFn.invoker();
    }
  };
  wrapFn.now = (...args) => {
    wrapFn.cancel();
    wrapFn.args = args;
    wrapFn.invoker();
  };
  wrapFn.resume = (all) => {
    const n = wrapFn.suspended;
    wrapFn.suspended = all || n < 1 ? 0 : n - 1;
  };
  wrapFn.suspend = () => {
    ++wrapFn.suspended;
  };
  wrapFn.immediate = false;
  wrapFn.suspended = wrapFn.suspended ? 1 : 0;
  wrapFn.timerId = null;
  defineProperty(wrapFn, "isPending", {
    get() {
      return wrapFn.timerId !== null;
    }
  });
  return wrapFn;
};
var Delayable_default = (Target) => {
  var _a;
  return _a = class extends (Target || Base) {
    static get declarable() {
      return [
        /**
         * This class property returns an object that specifies methods to wrap with configurable timer behaviors.
         *
         * It is used like so:
         * ```javascript
         *  class Foo extends Base.mixin(Delayable) {
         *      static get delayable() {
         *          return {
         *              expensiveMethod : 500
         *          };
         *      }
         *
         *      expensiveMethod() {
         *          this.things();
         *          this.moreThings();
         *          this.evenMoreThings();
         *      }
         *  }
         * ```
         * With the above in place, consider:
         * ```javascript
         *  let instance = new Foo();
         *
         *  instance.expensiveMethod();
         * ```
         * Instead of the above code immediately calling the `expensiveMethod()`, it will start a timer that will
         * invoke the method 500ms later. Because `expensiveMethod()` is an instance method, each instance of `Foo`
         * will have its own timer.
         *
         * NOTE: Only instance methods are currently supported (i.e., only non-`static` methods).
         *
         * #### Options
         * The value of each key configures how the method will be scheduled. If the value is a number, it is
         * promoted to a config object of `type='buffer'` as in the following:
         * ```javascript
         *  class Foo extends Base.mixin(Delayable) {
         *      static get delayable() {
         *          return {
         *              expensiveMethod : {
         *                  type  : 'buffer',
         *                  delay : 500
         *              }
         *          };
         *      }
         *  }
         * ```
         * The `type` property of the config object must be one of three values. Other options can be provided
         * depending on the `type`:
         *
         *  - `buffer`<br>
         *    Other options:
         *     - `delay` (Number) : The number of milliseconds to wait before calling the underlying method. A
         *       value of 0 is equivalent to setting `immediate: true`.
         *     - `immediate` (Boolean) : Set to `true` to call immediately (effectively disabling the buffer).
         *  - `raf` (short for "request animation frame")<br>
         *  - `idle` (short for "request idle callback") __Not available on Safari__ <br>
         *    Other options:
         *     - `cancelOutstanding` (Boolean) : Set to `true` to cancel any pending animation frame requests and
         *       schedule a new one on each call.
         *     - `immediate` (Boolean) : Set to `true` to call immediately.
         *  - `throttle`<br>
         *    Other options:
         *     - `delay` (Number) : The number of milliseconds to wait after each execution before another
         *       execution takes place. A value of 0 is equivalent to setting `immediate: true`.
         *     - `immediate` (Boolean) : Set to `true` to call immediately (effectively disabling the throttle).
         *
         * While `immediate: true` can be specified at the class level, it is more typical to set it on the
         * instance's method as described below.
         *
         * #### Delayable Method API
         * Delayable methods have a consistent API to manage their scheduling. This API is added to the methods
         * themselves.
         *
         * For example:
         * ```javascript
         *  let instance = new Foo();
         *
         *  instance.expensiveMethod();         // schedule a call in 500ms
         *  instance.expensiveMethod.isPending; // true
         *  instance.expensiveMethod.cancel();
         *  instance.expensiveMethod.flush();
         *  instance.expensiveMethod.now();
         *
         *  instance.expensiveMethod.delay = 10;
         *  instance.expensiveMethod();         // schedule a call in 10ms
         * ```
         *
         * ##### `isPending` (Boolean, readonly)
         * This boolean property will be `true` if a call has been scheduled, and false otherwise.
         *
         * ##### `cancel()`
         * Cancels a pending call if one has been scheduled. Otherwise this method does nothing.
         *
         * ##### `flush()`
         * Cancels the timer and causes the pending call to execute immediately. If there is no pending call, this
         * method does nothing.
         *
         * ##### `now()`
         * Cancels the timer (if one is pending) and executes the method immediately. If there is no pending call,
         * this method will still call the underlying method.
         *
         * @static
         * @member {Object<String,'raf'|Number|DelayableConfig>} delayable
         * @internal
         */
        "delayable"
      ];
    }
    doDestroy() {
      super.doDestroy();
      this.clearDelayables();
    }
    clearDelayables() {
      const me = this, [idleCallbackIds, animationFrameIds, intervalIds, timeoutMap, timeoutIds, microtasksPending] = [
        me.idleCallbackIds,
        me.animationFrameIds,
        me.intervalIds,
        me.timeoutMap,
        me.timeoutIds,
        me.microtasksPending
      ];
      me.idleCallbackIds = me.animationFrameIds = me.intervalIds = me.timeoutMap = me.timeoutIds = me.microtasksPending = null;
      timeoutIds == null ? void 0 : timeoutIds.forEach((fn, id) => {
        if (typeof fn === "function") {
          fn();
        }
        clearTimeout(id);
        globalDelays == null ? void 0 : globalDelays.timeouts.delete(id);
      });
      timeoutMap == null ? void 0 : timeoutMap.forEach((name, id) => clearTimeout(id));
      intervalIds == null ? void 0 : intervalIds.forEach((id) => {
        clearInterval(id);
        globalDelays == null ? void 0 : globalDelays.intervals.delete(id);
      });
      animationFrameIds == null ? void 0 : animationFrameIds.forEach((id) => {
        cancelAnimationFrame(id);
        globalDelays == null ? void 0 : globalDelays.animationFrames.delete(id);
      });
      idleCallbackIds == null ? void 0 : idleCallbackIds.forEach((id) => {
        cancelIdleCallback(id);
        globalDelays == null ? void 0 : globalDelays.idleCallbacks.delete(id);
      });
      microtasksPending == null ? void 0 : microtasksPending.forEach((taskDef) => taskDef.cancel());
    }
    /**
     * Check if a named timeout is active
     * @param name
     * @internal
     */
    hasTimeout(name) {
      var _a2;
      return Boolean((_a2 = this.timeoutMap) == null ? void 0 : _a2.has(name));
    }
    /**
     * Returns a function that when called will schedule a call to `fn` via {@link #function-queueMicrotask}.
     *
     * @param {Function|String} fn The function to call. If this is a string, it is looked up as a method on `this`
     * instance (or `options.thisObj` instead, if provided).
     * @param {Boolean|Object} [options] An options object.
     * @param {Array} [options.appendArgs] The argument list to append to those passed to the function.
     * @param {Object} [options.thisObj] The `this` reference for the function.
     * @returns {Function}
     * @internal
     */
    asap(fn, options) {
      const me = this, asapWrapFn = (...params) => {
        if (!asapWrapFn.suspended) {
          asapWrapFn.called = false;
          asapWrapFn.args = params;
          if (asapWrapFn.immediate) {
            invoker();
          } else if (!asapWrapFn.isPending) {
            asapWrapFn.timerId = me.queueMicrotask(invoker);
          }
        }
      }, invoker = makeInvoker(me, fn, asapWrapFn, options);
      return decorateWrapFn(me, asapWrapFn, "cancelMicrotask");
    }
    /**
     * Equivalent to the native [`queueMicrotask`](https://developer.mozilla.org/en-US/docs/Web/API/queueMicrotask), but
     * will be cancelled automatically on `destroy`. When `queueMicrotask` is not available, the provided `fn` is
     * called using `Promise.resolve().then(fn)`, which behaves similarly.
     *
     * Returns a function that when called will cancel the impending call. This function can also be viewed as a timer
     * id that can be passed to {@link #function-cancelMicrotask}
     *
     * @param {Function|String} fn The function to call, or name of function in this object to call.
     * @param {Array} [args] The arguments to pass.
     * @param {Object} [options] An object containing the details about that function, and the time delay.
     * @param {Array} [options.args] The arguments to pass if
     * @param {Boolean} [options.runOnDestroy] Pass `true` if this function should be executed if the Delayable instance
     * is destroyed while function is scheduled.
     * @param {Boolean} [options.cancelOutstanding] Pass `true` to cancel any outstanding invocation of the passed function.
     * @returns {Function}
     * @internal
     */
    queueMicrotask(fn, args, options) {
      let key, taskDef;
      if (!options && args && !Array.isArray(args)) {
        options = args;
        args = null;
      }
      options = options || emptyObject;
      args = args || options.args || emptyArray;
      const me = this, string = typeof fn === "string", name = string ? fn : fn.name, pending = me.microtasksPending || (me.microtasksPending = /* @__PURE__ */ new Map()), cancel = () => pending.delete(key), wrapFn = () => {
        if (taskDef === pending.get(key)) {
          pending.delete(key);
          if (string) {
            !me.isDestroyed && me[fn](...args);
          } else {
            fn.apply(me, args);
          }
        }
      };
      if (me.isDestroying && !options.runOnDestroy) {
        return emptyFn;
      }
      key = options.cancelOutstanding ? name || fn : cancel;
      taskDef = { key, name, fn, args, options, cancel, wrapFn };
      cancel.taskDef = taskDef;
      queueMicrotask ? queueMicrotask(wrapFn) : asapPromise.then(wrapFn);
      pending.set(key, taskDef);
      return cancel;
    }
    /**
     * Given the return value from a call to {@link #function-queueMicrotask}, cancels the pending call. This method is
     * provided for symmetry with other timer functions, such as {@link #function-setTimeout}.
     *
     * @param {Function} task The value returned by `queueMicrotask`.
     * @internal
     */
    cancelMicrotask(task) {
      task == null ? void 0 : task();
    }
    /**
     * Same as native setTimeout, but will be cleared automatically on destroy. If a propertyName is supplied it will
     * be used to store the timeout id.
     * @param {Object} timeoutSpec An object containing the details about that function, and the time delay.
     * @param {Function|String} timeoutSpec.fn The function to call, or name of function in this object to call. Used as the `name` parameter if a string.
     * @param {Number} timeoutSpec.delay The milliseconds to delay the call by.
     * @param {Object[]} timeoutSpec.args The arguments to pass.
     * @param {String} [timeoutSpec.name] The name under which to register the timer. Defaults to `fn.name`.
     * @param {Boolean} [timeoutSpec.runOnDestroy] Pass `true` if this function should be executed if the Delayable instance is destroyed while function is scheduled.
     * @param {Boolean} [timeoutSpec.cancelOutstanding] Pass `true` to cancel any outstanding invocation of the passed function.
     * @returns {Number}
     * @internal
     */
    setTimeout({ fn, delay, name, runOnDestroy, cancelOutstanding, args }) {
      if (arguments.length > 1 || typeof arguments[0] === "function") {
        [fn, delay, name, runOnDestroy, cancelOutstanding] = arguments;
      }
      if (typeof fn === "string") {
        name = fn;
      } else if (!name) {
        name = fn.name || fn;
      }
      if (cancelOutstanding) {
        this.clearTimeout(name);
      }
      const me = this, timeoutIds = me.timeoutIds || (me.timeoutIds = /* @__PURE__ */ new Map()), timeoutMap = me.timeoutMap || (me.timeoutMap = /* @__PURE__ */ new Map()), timeoutId = setTimeout(() => {
        if (typeof fn === "string") {
          fn = me[name];
        }
        timeoutIds == null ? void 0 : timeoutIds.delete(timeoutId);
        timeoutMap == null ? void 0 : timeoutMap.delete(name);
        globalDelays == null ? void 0 : globalDelays.timeouts.delete(timeoutId);
        fn.apply(me, args);
      }, delay);
      timeoutIds.set(timeoutId, runOnDestroy ? fn : true);
      globalDelays == null ? void 0 : globalDelays.timeouts.set(timeoutId, {
        fn,
        delay,
        name
        /*, stack : new Error().stack*/
      });
      if (name) {
        timeoutMap.set(name, timeoutId);
      }
      return timeoutId;
    }
    /**
     * clearTimeout wrapper, either call with timeout id as normal clearTimeout or with timeout name (if you specified
     * a name to setTimeout())
     * property to null.
     * @param {Number|String} idOrName timeout id or name
     * @internal
     */
    clearTimeout(idOrName) {
      var _a2;
      let id = idOrName;
      if (typeof id === "string") {
        if (this.timeoutMap) {
          id = this.timeoutMap.get(idOrName);
          this.timeoutMap.delete(idOrName);
        } else {
          return;
        }
      }
      clearTimeout(id);
      (_a2 = this.timeoutIds) == null ? void 0 : _a2.delete(id);
      globalDelays == null ? void 0 : globalDelays.timeouts.delete(id);
    }
    /**
     * clearInterval wrapper
     * @param {Number} id
     * @internal
     */
    clearInterval(id) {
      var _a2;
      clearInterval(id);
      (_a2 = this.intervalIds) == null ? void 0 : _a2.delete(id);
      globalDelays == null ? void 0 : globalDelays.intervals.delete(id);
    }
    /**
     * Same as native setInterval, but will be cleared automatically on destroy
     * @param {Function} fn callback method
     * @param {Number} delay delay in milliseconds
     * @param {String} name delay name for debugging
     * @returns {Number}
     * @internal
     */
    setInterval(fn, delay, name) {
      const intervalId = setInterval(fn, delay);
      (this.intervalIds || (this.intervalIds = /* @__PURE__ */ new Set())).add(intervalId);
      globalDelays == null ? void 0 : globalDelays.intervals.set(intervalId, { fn, delay, name });
      return intervalId;
    }
    /**
     * Relays to native requestAnimationFrame and adds to tracking to have call automatically canceled on destroy.
     * @param {Function} fn
     * @param {Object[]} [extraArgs] The argument list to append to those passed to the function.
     * @param {Object} [thisObj] `this` reference for the function.
     * @returns {Number}
     * @internal
     */
    requestAnimationFrame(fn, extraArgs = [], thisObj = this) {
      const animationFrameIds = this.animationFrameIds || (this.animationFrameIds = /* @__PURE__ */ new Set()), frameId = requestAnimationFrame(() => {
        globalDelays == null ? void 0 : globalDelays.animationFrames.delete(frameId);
        animationFrameIds.delete(frameId) && fn.apply(thisObj, extraArgs);
      });
      animationFrameIds.add(frameId);
      globalDelays == null ? void 0 : globalDelays.animationFrames.set(frameId, { fn, extraArgs, thisObj });
      return frameId;
    }
    /**
     * Relays to native requestIdleCallback and adds to tracking to have call automatically canceled on destroy.
     * @param {Function} fn
     * @param {Object[]} [extraArgs] The argument list to append to those passed to the function.
     * @param {Object} [thisObj] `this` reference for the function.
     * @returns {Number}
     * @internal
     */
    requestIdleCallback(fn, extraArgs = [], thisObj = this) {
      const idleCallbackIds = this.idleCallbackIds || (this.idleCallbackIds = /* @__PURE__ */ new Set()), frameId = requestIdleCallback(() => {
        globalDelays == null ? void 0 : globalDelays.idleCallbacks.delete(frameId);
        idleCallbackIds.delete(frameId) && fn.apply(thisObj, extraArgs);
      });
      idleCallbackIds.add(frameId);
      globalDelays == null ? void 0 : globalDelays.idleCallbacks.set(frameId, { fn, extraArgs, thisObj });
      return frameId;
    }
    /**
     * Creates a function which will execute once, on the next animation frame. However many time it is
     * called in one event run, it will only be scheduled to run once.
     * @param {Function|String} fn The function to call, or name of function in this object to call.
     * @param {Object[]} [args] The argument list to append to those passed to the function.
     * @param {Object} [thisObj] `this` reference for the function.
     * @param {Boolean} [cancelOutstanding] Cancel any outstanding queued invocation upon call.
     * @internal
     */
    createOnFrame(fn, args = [], thisObj = this, cancelOutstanding) {
      let rafId;
      const result = (...callArgs) => {
        if (rafId != null && cancelOutstanding) {
          this.cancelAnimationFrame(rafId);
          rafId = null;
        }
        if (rafId == null) {
          rafId = this.requestAnimationFrame(() => {
            if (typeof fn === "string") {
              fn = thisObj[fn];
            }
            rafId = null;
            callArgs.push(...args);
            fn.apply(thisObj, callArgs);
          });
        }
      };
      result.cancel = () => this.cancelAnimationFrame(rafId);
      return result;
    }
    /**
     * Relays to native cancelAnimationFrame and removes from tracking.
     * @param {Number} handle
     * @internal
     */
    cancelAnimationFrame(handle) {
      var _a2;
      cancelAnimationFrame(handle);
      (_a2 = this.animationFrameIds) == null ? void 0 : _a2.delete(handle);
      globalDelays == null ? void 0 : globalDelays.animationFrames.delete(handle);
    }
    /**
     * Relays to native cancelIdleCallback and removes from tracking.
     * @param {Number} handle
     * @internal
     */
    cancelIdleCallback(handle) {
      var _a2;
      cancelIdleCallback(handle);
      (_a2 = this.idleCallbackIds) == null ? void 0 : _a2.delete(handle);
      globalDelays == null ? void 0 : globalDelays.idleCallbacks.delete(handle);
    }
    async nextAnimationFrame() {
      return new Promise((resolve) => this.requestAnimationFrame(resolve));
    }
    /**
     * Wraps a function with another function that delays it specified amount of time, repeated calls to the wrapper
     * resets delay.
     * @param {Function|String} fn The function to call. If this is a string, it is looked up as a method on `this`
     * instance (or `options.thisObj` instead, if provided).
     * @param {Object|Number} options The delay in milliseconds or an options object.
     * @param {Number} options.delay The delay in milliseconds.
     * @param {Array} [options.appendArgs] The argument list to append to those passed to the function.
     * @param {Object} [options.thisObj] The `this` reference for the function.
     * @returns {Function} Wrapped function to call.
     * @internal
     */
    buffer(fn, options) {
      let delay = options;
      if (options && typeof options !== "number") {
        delay = options.delay;
      } else {
        options = null;
      }
      const bufferWrapFn = (...params) => {
        if (bufferWrapFn.suspended) {
          return;
        }
        const { delay: delay2 } = bufferWrapFn;
        bufferWrapFn.cancel();
        bufferWrapFn.called = false;
        bufferWrapFn.args = params;
        if (bufferWrapFn.immediate || !delay2) {
          invoker();
        } else {
          bufferWrapFn.timerId = this.setTimeout(invoker, delay2);
        }
      }, invoker = makeInvoker(this, fn, bufferWrapFn, options);
      bufferWrapFn.delay = delay;
      return decorateWrapFn(this, bufferWrapFn);
    }
    /**
     * Returns a function that when called will schedule a call to `fn` on the next animation frame.
     *
     * @param {Function|String} fn The function to call. If this is a string, it is looked up as a method on `this`
     * instance (or `options.thisObj` instead, if provided).
     * @param {Boolean|Object} [options] An options object or the `cancelOutstanding` boolean property of it.
     * @param {Boolean} [options.cancelOutstanding] Pass `true` to cancel any pending animation frame requests and
     * schedule a new one on each call to the returned function.
     * @param {Array} [options.appendArgs] The argument list to append to those passed to the function.
     * @param {Object} [options.thisObj] The `this` reference for the function.
     * @returns {Function}
     * @internal
     */
    raf(fn, options) {
      let cancelOutstanding = options;
      if (options && typeof options !== "boolean") {
        cancelOutstanding = options.cancelOutstanding;
      } else {
        options = null;
      }
      const rafWrapFn = (...params) => {
        if (rafWrapFn.suspended) {
          return;
        }
        if (rafWrapFn.cancelOutstanding || rafWrapFn.immediate) {
          rafWrapFn.cancel();
        }
        rafWrapFn.called = false;
        rafWrapFn.args = params;
        if (rafWrapFn.immediate) {
          invoker();
        } else if (!rafWrapFn.isPending) {
          rafWrapFn.timerId = this.requestAnimationFrame(invoker);
        }
      }, invoker = makeInvoker(this, fn, rafWrapFn, options);
      rafWrapFn.cancelOutstanding = cancelOutstanding;
      rafWrapFn.raf = true;
      return decorateWrapFn(this, rafWrapFn, "cancelAnimationFrame");
    }
    idle(fn, options) {
      let cancelOutstanding = options;
      if (options && typeof options !== "boolean") {
        cancelOutstanding = options.cancelOutstanding;
      } else {
        options = null;
      }
      const idleWrapFn = (...params) => {
        if (idleWrapFn.suspended) {
          return;
        }
        if (idleWrapFn.cancelOutstanding || idleWrapFn.immediate) {
          idleWrapFn.cancel();
        }
        idleWrapFn.called = false;
        idleWrapFn.args = params;
        if (idleWrapFn.immediate) {
          invoker();
        } else if (!idleWrapFn.isPending) {
          idleWrapFn.timerId = this.requestIdleCallback(invoker);
        }
      }, invoker = makeInvoker(this, fn, idleWrapFn, options);
      idleWrapFn.cancelOutstanding = cancelOutstanding;
      this.setTimeout(() => this.idleCallbackIds.delete(idleWrapFn.timerId) && idleWrapFn.now(), 100);
      return decorateWrapFn(this, idleWrapFn, "cancelIdleCallback");
    }
    /**
     * Create a "debounced" function which will call on the "leading edge" of a timer period.
     * When first invoked will call immediately, but invocations after that inside its buffer
     * period will be rejected, and *one* invocation will be made after the buffer period has expired.
     *
     * This is useful for responding immediately to a first mousemove, but from then on, only
     * calling the action function on a regular timer while the mouse continues to move.
     *
     * @param {Function|String} fn The function to call. If this is a string, it is looked up as a method on `this`
     * instance (or `options.thisObj` instead, if provided).
     * @param {Number|Object} options The milliseconds to wait after each execution before another execution takes place
     * or a object containing options.
     * @param {Object} [options.thisObj] `this` reference for the function.
     * @param {Array} [options.appendArgs] The argument list to append to those passed to the function.
     * @param {Function|String} [options.throttled] A function to call when the invocation is delayed due to buffer
     * time not having expired. If this is a string, it is looked up as a method on `this` instance (or `options.thisObj`
     * instead, if provided). When called, the same arguments are passed as would have been passed to `fn`, including
     * any `options.appendArgs`.
     * @internal
     */
    throttle(fn, options) {
      let delay = options, throttled;
      if (options && typeof options !== "number") {
        delay = options.delay;
        throttled = options.throttled;
      } else {
        options = null;
      }
      const me = this, throttleWrapFn = (...args) => {
        if (throttleWrapFn.suspended) {
          return;
        }
        const { delay: delay2 } = throttleWrapFn, elapsed = performance.now() - throttleWrapFn.lastCallTime;
        throttleWrapFn.args = args;
        if (throttleWrapFn.immediate || elapsed >= delay2) {
          me.clearTimeout(throttleWrapFn.timerId);
          invoker();
        } else {
          if (!throttleWrapFn.isPending) {
            throttleWrapFn.timerId = me.setTimeout(invoker, delay2 - elapsed);
            throttleWrapFn.called = false;
          }
          if (throttled) {
            throttled.wrapFn.args = args;
            throttled();
          }
        }
      }, invoker = makeInvoker(me, fn, throttleWrapFn, options);
      throttleWrapFn.delay = delay;
      if (throttled) {
        throttled = makeInvoker(me, throttled, () => {
        }, options);
      }
      return decorateWrapFn(me, throttleWrapFn);
    }
    static setupDelayable(cls) {
      cls.setupDelayableMethods(cls.delayable);
    }
    /**
     * This method initializes the `delayable` methods on this class.
     * @param {Object} delayable The `delayable` property.
     * @param {Function} [cls] This parameter will be used internally to process static methods.
     * @private
     */
    static setupDelayableMethods(delayable, cls = null) {
      const me = this, statics = delayable.static, target = cls || me.prototype;
      if (statics) {
        delete delayable.static;
      }
      for (const name in delayable) {
        let options = delayable[name];
        const implName = name + "Now", type = typeof options;
        if (!target[implName]) {
          target[implName] = target[name];
        }
        if (type === "number") {
          options = {
            type: "buffer",
            delay: options
          };
        } else if (type === "string") {
          options = {
            type: options
          };
        }
        defineProperty(target, name, {
          get() {
            const me2 = this, caller = (...params) => me2[implName](...params), value = me2[options.type](caller, options);
            defineProperty(me2, name, { value, configurable: true, writable: true });
            caller.methodName = name;
            return value;
          }
        });
      }
    }
    // This does not need a className on Widgets.
    // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
    // to the Widget it's mixed in to should implement this.
    get widgetClass() {
    }
  }, __publicField(_a, "$name", "Delayable"), _a;
};
export {
  Delayable_default as default
};
